import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Text as RNText, Alert, Platform, TouchableOpacity } from 'react-native';
import { Card, Text, Chip, Searchbar, FAB, Menu, Divider, IconButton, Button } from 'react-native-paper';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ReviewStackParamList } from '../../navigation/AppNavigator';
import { RootState, AppDispatch } from '../../store';
import { fetchReviews, fetchTemplates, deleteReview } from '../../store/slices/reviewSlice';
import { colors, spacing, typography, shadows } from '../../constants/theme';
import log from '../../utils/logger';
import { isDetoxTestControlsEnabled } from '../../config/environments';

import { HomeReview } from '../../types';

// Define ReviewStatus values directly to avoid import issues on web
const ReviewStatusValues = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
} as const;

// Import MaterialCommunityIcons conditionally for web compatibility
let MaterialCommunityIcons: any;
try {
  MaterialCommunityIcons = require('@expo/vector-icons').MaterialCommunityIcons;
} catch (e) {
  // Fallback for web - create a simple text-based icon component
  MaterialCommunityIcons = ({ name, size, color, ...props }: any) => {
    const iconMap: { [key: string]: string } = {
      'home': '🏠',
      'clipboard-list': '📋',
      'file-document-outline': '📄',
      'check-circle': '✓',
      'clock-outline': '⏱',
      'filter-variant': '▼',
      'magnify': '🔍',
      'plus': '+',
    };
    return <RNText style={{ fontSize: size || 24, color }} {...props}>{iconMap[name] || '•'}</RNText>;
  };
}

type NavigationProp = StackNavigationProp<ReviewStackParamList, 'ReviewList'>;

const getTemplateFilterSlug = (templateName: string) =>
  templateName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'unnamed-template';

const ReviewListScreen: React.FC = () => {
  
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch<AppDispatch>();
  const listRef = useRef<FlatList<HomeReview>>(null);
  const { reviews, templates, isLoading } = useSelector((state: RootState) => state.review);
  const { user } = useSelector((state: RootState) => state.auth);
  const { isOnline } = useSelector((state: RootState) => state.offline);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedTemplateNames, setSelectedTemplateNames] = useState<string[]>([]);
  const [showTemplateFilterMenu, setShowTemplateFilterMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState<number | null>(null);

  useEffect(() => {
    try {
      dispatch(fetchReviews());
      dispatch(fetchTemplates(false));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [dispatch]);

  // Refresh reviews when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      dispatch(fetchReviews());
      dispatch(fetchTemplates(false));
    }, [dispatch])
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      dispatch(fetchReviews()),
      dispatch(fetchTemplates(false)),
    ]);
    setRefreshing(false);
  };

  useEffect(() => {
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }, [searchQuery, filterStatus, selectedTemplateNames]);

  const templateFilterOptions = useMemo(() => {
    if (!Array.isArray(templates)) {
      return [];
    }

    const seen = new Set<string>();
    const options: string[] = [];

    templates.forEach(template => {
      const name = template?.name;
      if (typeof name === 'string' && name.length > 0 && !seen.has(name)) {
        seen.add(name);
        options.push(name);
      }
    });

    return options;
  }, [templates]);

  const templateNameById = useMemo(() => {
    const names = new Map<number, string>();

    if (Array.isArray(templates)) {
      templates.forEach(template => {
        if (template?.id != null && typeof template.name === 'string' && template.name.length > 0) {
          names.set(Number(template.id), template.name);
        }
      });
    }

    return names;
  }, [templates]);

  const getReviewTemplateName = (review: HomeReview): string | undefined => {
    if (typeof review?.template?.name === 'string' && review.template.name.length > 0) {
      return review.template.name;
    }

    const templateId = review?.templateId ?? review?.template?.id;
    return templateId != null ? templateNameById.get(Number(templateId)) : undefined;
  };

  const toggleTemplateName = (templateName: string) => {
    setSelectedTemplateNames(current =>
      current.includes(templateName)
        ? current.filter(name => name !== templateName)
        : [...current, templateName]
    );
  };

  const getTemplateFilterLabel = () => {
    if (selectedTemplateNames.length === 0) {
      return 'Filter: All';
    }

    if (selectedTemplateNames.length === 1) {
      return selectedTemplateNames[0];
    }

    return `Filter: ${selectedTemplateNames.length}`;
  };

  const getFilteredReviews = () => {
    if (!Array.isArray(reviews)) {
      return [];
    }

    let filtered = reviews.filter(review => review != null); // Filter out null/undefined reviews

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(review => review?.status === filterStatus);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        review => {
          if (!review) return false;
          const propertyAddress = review.property?.address?.toLowerCase() || '';
          const reviewAddress = review.address?.toLowerCase() || '';
          const companyName = review.property?.company?.name?.toLowerCase() || '';
          const memberName = review.memberName?.toLowerCase() || '';
          const homeManagerName = review.homeManagerName?.toLowerCase() || '';
          return propertyAddress.includes(query) || reviewAddress.includes(query) ||
            companyName.includes(query) || memberName.includes(query) || homeManagerName.includes(query);
        }
      );
    }

    // Filter by template name
    if (selectedTemplateNames.length > 0) {
      filtered = filtered.filter(review => {
        const templateName = getReviewTemplateName(review);
        return typeof templateName === 'string' && selectedTemplateNames.includes(templateName);
      });
    }

    // Sort by date (most recent first)
    return filtered.sort((a, b) => {
      try {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      } catch (error) {
        console.error('Error sorting reviews:', error);
        return 0;
      }
    });
  };

  const getStatusColor = (status: string) => {
    try {
      switch (status) {
        case ReviewStatusValues.DRAFT:
          return colors.gray500;
        case ReviewStatusValues.IN_PROGRESS:
          return colors.warning;
        case ReviewStatusValues.COMPLETED:
          return colors.info;
        case ReviewStatusValues.SUBMITTED:
          return colors.success;
        default:
          return colors.gray500;
      }
    } catch (error) {
      console.error('[REVIEWLIST] getStatusColor - ERROR:', error);
      return colors.gray500;
    }
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  const handleDeleteReview = (reviewId: number, address: string) => {
    // Check if online before allowing deletion
    if (!isOnline) {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('Review deletion requires an internet connection. Please connect and try again.');
      } else if (Alert && Alert.alert) {
        Alert.alert(
          'Cannot Delete Offline',
          'Review deletion requires an internet connection. Please connect and try again.',
          [{ text: 'OK' }]
        );
      }
      return;
    }

    try {
      // For web compatibility, use window.confirm instead of Alert.alert
      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        const confirmed = window.confirm(`Are you sure you want to delete the review for ${address}? This action cannot be undone.`);
        if (confirmed) {
          setDeletingReviewId(reviewId);
          dispatch(deleteReview(reviewId))
            .unwrap()
            .then(() => {
              setDeletingReviewId(null);
            })
            .catch((error) => {
              setDeletingReviewId(null);
              if (typeof window.alert === 'function') {
                window.alert('Failed to delete review. Please try again.');
              }
            });
        }
      } else if (Alert && Alert.alert) {
        // Mobile - check that Alert.alert exists
        // Show the alert directly without setTimeout
        Alert.alert(
          'Delete Review',
          `Are you sure you want to delete the review for ${address}? This action cannot be undone.`,
          [
            {
              text: 'Cancel',
              style: 'cancel',
            },
            {
              text: 'Delete',
              style: 'destructive',
              onPress: () => {
                setDeletingReviewId(reviewId);
                dispatch(deleteReview(reviewId))
                  .unwrap()
                  .then(() => {
                    setDeletingReviewId(null);
                  })
                  .catch((error) => {
                    setDeletingReviewId(null);
                    Alert.alert('Error', 'Failed to delete review. Please try again.');
                  });
              }
            }
          ],
          { cancelable: true }
        );
      }
    } catch (error) {
      console.error('[ReviewListScreen] Error in handleDeleteReview:', error);
    }
  };

  const renderReviewItem = ({ item }: { item: HomeReview }) => {
    if (!item) return null;

    const handleCardPress = () => {
      // Prevent navigation if deletion is in progress
      if (deletingReviewId === item.id) {
        return;
      }

      navigation.navigate('ReviewDetail', { reviewId: item.id });
    };

    const handleDelete = (e?: any) => {
      // Stop all event propagation for web
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault();
        }
        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }
        if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
          e.nativeEvent.stopImmediatePropagation();
        }
      }

      // Call handleDeleteReview directly
      handleDeleteReview(item.id, item?.property?.address || item?.address || 'Unknown Address');
    };

    return (
    <TouchableOpacity
      testID={`review-card-${item.id}`}
      onPress={handleCardPress}
      activeOpacity={0.95}
    >
      <Card
        style={styles.reviewCard}
        // Remove onPress from Card to avoid conflicts
      >
        <Card.Content>
          <View style={styles.reviewHeader}>
            <View style={styles.reviewInfo}>
              <Text style={styles.reviewTitle}>
                {item?.property?.address || item?.address || 'No address'}
              </Text>
              <Text testID={`review-card-member-name-${item.id}`} style={styles.memberName}>
                {item.memberName || 'Member name not set'}
              </Text>
              {getReviewTemplateName(item) && (
                <Text style={styles.reviewType}>
                  Type: {getReviewTemplateName(item)}
                </Text>
              )}
            </View>
            <View style={styles.chipContainer}>
              <Chip
                mode="flat"
                style={[styles.statusChip, { backgroundColor: getStatusColor(item?.status || 'draft') }]}
                textStyle={styles.statusText}
              >
                {(item?.status || 'draft').replace(/_/g, ' ').toUpperCase()}
              </Chip>
              {user?.role === 'admin' && (
                <View style={{ padding: 8 }}>
                  <IconButton
                    testID={`review-delete-button-${item.id}`}
                    icon="delete"
                    size={20}
                    iconColor={isOnline ? colors.error : colors.gray400}
                    disabled={!isOnline || deletingReviewId === item.id}
                    onPress={handleDelete}
                    style={{ margin: 0, opacity: isOnline ? 1 : 0.5 }}
                  />
                </View>
              )}
            </View>
          </View>
        
        <Divider style={styles.divider} />
        
        <View style={styles.reviewFooter}>
          <View style={styles.dateContainer}>
            <MaterialCommunityIcons
              name="calendar"
              size={16}
              color={colors.textSecondary}
            />
            <Text style={styles.dateText}>
              Started: {formatDate(item?.startedAt)}
            </Text>
          </View>
          {item?.submittedAt && (
            <View style={styles.dateContainer}>
              <MaterialCommunityIcons
                name="check-circle"
                size={16}
                color={colors.success}
              />
              <Text style={styles.dateText}>
                Submitted: {formatDate(item?.submittedAt)}
              </Text>
            </View>
          )}
        </View>

        {/* Progress indicator for in-progress reviews */}
        {item?.status === ReviewStatusValues.IN_PROGRESS && (
          <View style={styles.progressContainer}>
            <Text style={styles.progressText}>
              {(() => {
                try {
                  // Helper function to recursively get all items from categories and subcategories
                  const getAllItems = (categoryList: any[]): any[] => {
                    if (!Array.isArray(categoryList)) return [];
                    const allItems: any[] = [];
                    categoryList.forEach(category => {
                      if (!category) return;
                      // Add items from this category
                      const categoryItems = category.items || [];
                      allItems.push(...categoryItems);
                      
                      // Recursively add items from subcategories
                      const subcategories = category.subcategories || [];
                      allItems.push(...getAllItems(subcategories));
                    });
                    return allItems;
                  };

                  // Get all template items from categories recursively
                  const categories = item?.template?.categories || [];
                  const allItems = getAllItems(categories);
                  const completedResponses = item?.responses?.filter(r => r?.status === 'completed').length || 0;
                  return `${completedResponses} / ${allItems.length} items completed`;
                } catch (error) {
                  console.error('Error calculating progress:', error);
                  return 'Progress unavailable';
                }
              })()}
            </Text>
          </View>
        )}
      </Card.Content>
    </Card>
    </TouchableOpacity>
  );
  };

  const filteredReviews = getFilteredReviews();
  const showTemplateFilter = templateFilterOptions.length > 0;
  const useTemplateChipFilter = templateFilterOptions.length <= 3;

  return (
    <View testID="review-list-screen" style={styles.container}>
      {/* Search and Filter */}
      <View style={styles.searchContainer}>
        <Searchbar
          testID="review-search-input"
          placeholder="Search by address..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchbar}
        />
        <Menu
          visible={showFilterMenu}
          onDismiss={() => setShowFilterMenu(false)}
          anchor={
            <FAB
              testID="review-filter-button"
              icon="filter"
              small
              style={styles.filterButton}
              onPress={() => setShowFilterMenu(true)}
            />
          }
        >
          <Menu.Item
            testID="review-filter-all"
            onPress={() => {
              setFilterStatus('all');
              setShowFilterMenu(false);
            }}
            title="All Reviews"
            leadingIcon={filterStatus === 'all' ? 'check' : undefined}
          />
          <Divider />
          <Menu.Item
            testID="review-filter-draft"
            onPress={() => {
              setFilterStatus(ReviewStatusValues.DRAFT);
              setShowFilterMenu(false);
            }}
            title="Draft"
            leadingIcon={(() => {
              return filterStatus === ReviewStatusValues.DRAFT ? 'check' : undefined;
            })()}
          />
          <Menu.Item
            testID="review-filter-in-progress"
            onPress={() => {
              setFilterStatus(ReviewStatusValues.IN_PROGRESS);
              setShowFilterMenu(false);
            }}
            title="In Progress"
            leadingIcon={filterStatus === ReviewStatusValues.IN_PROGRESS ? 'check' : undefined}
          />
          <Menu.Item
            testID="review-filter-completed"
            onPress={() => {
              setFilterStatus(ReviewStatusValues.COMPLETED);
              setShowFilterMenu(false);
            }}
            title="Completed"
            leadingIcon={filterStatus === ReviewStatusValues.COMPLETED ? 'check' : undefined}
          />
          <Menu.Item
            testID="review-filter-submitted"
            onPress={() => {
              setFilterStatus(ReviewStatusValues.SUBMITTED);
              setShowFilterMenu(false);
            }}
            title="Submitted"
            leadingIcon={filterStatus === ReviewStatusValues.SUBMITTED ? 'check' : undefined}
          />
        </Menu>
      </View>

      {showTemplateFilter && (
        <View style={styles.templateFilterContainer}>
          {useTemplateChipFilter ? (
            templateFilterOptions.map(templateName => {
              const selected = selectedTemplateNames.includes(templateName);
              return (
                <TouchableOpacity
                  key={templateName}
                  testID={`review-template-filter-chip-${getTemplateFilterSlug(templateName)}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => toggleTemplateName(templateName)}
                  activeOpacity={0.85}
                  style={styles.templateFilterChipTouchTarget}
                >
                  <Chip
                    mode={selected ? 'flat' : 'outlined'}
                    selected={selected}
                    showSelectedCheck
                    style={[
                      styles.templateFilterChip,
                      selected && styles.templateFilterChipSelected,
                    ]}
                    textStyle={[
                      styles.templateFilterChipText,
                      selected && styles.templateFilterChipTextSelected,
                    ]}
                  >
                    {templateName}
                  </Chip>
                </TouchableOpacity>
              );
            })
          ) : (
            <Menu
              visible={showTemplateFilterMenu}
              onDismiss={() => setShowTemplateFilterMenu(false)}
              anchor={
                <Button
                  testID="review-template-filter-menu-button"
                  mode="outlined"
                  icon="filter-variant"
                  compact
                  onPress={() => setShowTemplateFilterMenu(true)}
                  style={styles.templateFilterMenuButton}
                  labelStyle={styles.templateFilterMenuLabel}
                >
                  {getTemplateFilterLabel()}
                </Button>
              }
            >
              <Menu.Item
                testID="review-template-filter-all"
                onPress={() => {
                  setSelectedTemplateNames([]);
                  setShowTemplateFilterMenu(false);
                }}
                title="All"
                leadingIcon={selectedTemplateNames.length === 0 ? 'check' : undefined}
              />
              <Divider />
              {templateFilterOptions.map(templateName => (
                <Menu.Item
                  key={templateName}
                  testID={`review-template-filter-option-${getTemplateFilterSlug(templateName)}`}
                  onPress={() => toggleTemplateName(templateName)}
                  title={templateName}
                  leadingIcon={selectedTemplateNames.includes(templateName) ? 'check' : undefined}
                />
              ))}
            </Menu>
          )}
        </View>
      )}
      {isDetoxTestControlsEnabled && (
        <Text testID="review-filtered-review-ids" style={styles.detoxStateMarker}>
          {`status=${filterStatus}|templates=${selectedTemplateNames.join(',')}|reviews=${filteredReviews
            .map((review) => `${review.id}:${review.property?.address || review.address || ''}`)
            .join('|')}`}
        </Text>
      )}

      {/* Review List */}
      <FlatList
        ref={listRef}
        testID="review-list-flatlist"
        data={filteredReviews}
        renderItem={renderReviewItem}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={styles.listContent}
        removeClippedSubviews={false}
        initialNumToRender={10}
        maxToRenderPerBatch={10}
        windowSize={10}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons
              name="clipboard-text-outline"
              size={64}
              color={colors.gray400}
            />
            <Text style={styles.emptyText}>No reviews found</Text>
            <Text style={styles.emptySubtext}>
              {searchQuery || filterStatus !== 'all' || selectedTemplateNames.length > 0
                ? 'Try adjusting your filters'
                : 'Start a new review to get started'}
            </Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  searchContainer: {
    flexDirection: 'row',
    padding: spacing.md,
    alignItems: 'center',
  },
  searchbar: {
    flex: 1,
    marginRight: spacing.sm,
  },
  filterButton: {
    backgroundColor: colors.primary,
  },
  templateFilterContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    marginTop: -spacing.xs,
  },
  templateFilterChip: {
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  templateFilterChipTouchTarget: {
    marginRight: spacing.xs,
    marginBottom: spacing.sm,
  },
  templateFilterChipSelected: {
    backgroundColor: colors.primary,
  },
  templateFilterChipText: {
    color: colors.text,
    fontSize: typography.caption.fontSize,
    lineHeight: typography.caption.lineHeight,
  },
  templateFilterChipTextSelected: {
    color: '#fff',
  },
  templateFilterMenuButton: {
    borderColor: colors.border,
    maxWidth: '100%',
  },
  templateFilterMenuLabel: {
    color: colors.text,
  },
  listContent: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xl,
  },
  detoxStateMarker: {
    height: 1,
    opacity: 0.01,
  },
  reviewCard: {
    marginBottom: spacing.md,
    ...shadows.md,
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  reviewInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  reviewTitle: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  reviewType: {
    ...typography.caption,
    color: colors.primary,
    fontWeight: '500',
  },
  memberName: {
    ...typography.body2,
    color: colors.text,
    fontWeight: '600',
    marginTop: spacing.xs,
  },
  chipContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusChip: {
    minHeight: 32,
    paddingVertical: 6,
  },
  statusText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
    lineHeight: undefined,
    includeFontPadding: false,
  },
  divider: {
    marginVertical: spacing.sm,
  },
  reviewFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: spacing.md,
    marginTop: spacing.xs,
  },
  dateText: {
    ...typography.caption,
    color: colors.textSecondary,
    marginLeft: spacing.xs,
  },
  progressContainer: {
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  progressText: {
    ...typography.caption,
    color: colors.primary,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  emptyText: {
    ...typography.h4,
    color: colors.textSecondary,
    marginTop: spacing.md,
  },
  emptySubtext: {
    ...typography.body2,
    color: colors.gray500,
    marginTop: spacing.xs,
  },
});

export default ReviewListScreen;
